/** Guided preparation never starts or completes an exercise on the user's behalf. */
export const FITNESS_GUIDED_WARMUP_SECONDS = 120;
export interface FitnessGuidedPreparation {
  stage: 'introduction' | 'warmup' | 'workout';
  remainingSeconds: number;
}
export type FitnessGuidedPreparationAction = 'begin-warmup' | 'skip' | 'advance' | 'tick';
export const createFitnessGuidedPreparation = (): FitnessGuidedPreparation =>
  ({ stage: 'introduction', remainingSeconds: FITNESS_GUIDED_WARMUP_SECONDS });

export function readFitnessGuidedPreparation(input: unknown): FitnessGuidedPreparation {
  const invalid = (): never => { throw new Error('invalid_fitness_guided_preparation'); };
  if (!input || typeof input !== 'object' || Array.isArray(input)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(input))) return invalid();
  const descriptors = Object.getOwnPropertyDescriptors(input);
  if (Reflect.ownKeys(input).length !== 2 || !['stage', 'remainingSeconds'].every(key => descriptors[key] && 'value' in descriptors[key])) return invalid();
  const stage = descriptors.stage.value, remainingSeconds = descriptors.remainingSeconds.value;
  if (!['introduction', 'warmup', 'workout'].includes(stage) || !Number.isSafeInteger(remainingSeconds)
    || remainingSeconds < 0 || remainingSeconds > FITNESS_GUIDED_WARMUP_SECONDS
    || stage === 'introduction' && remainingSeconds !== FITNESS_GUIDED_WARMUP_SECONDS
    || stage === 'workout' && remainingSeconds !== 0) return invalid();
  return { stage, remainingSeconds };
}

export const isGuidedPreparationBlocking = (value?: FitnessGuidedPreparation): boolean =>
  value !== undefined && value.stage !== 'workout';

export function transitionFitnessGuidedPreparation(
  current: FitnessGuidedPreparation, action: FitnessGuidedPreparationAction,
): FitnessGuidedPreparation {
  const value = readFitnessGuidedPreparation(current);
  if (value.stage === 'workout') return current;
  if (action === 'skip' || action === 'advance' && value.stage === 'warmup') return { stage: 'workout', remainingSeconds: 0 };
  if (action === 'begin-warmup' && value.stage === 'introduction') return { stage: 'warmup', remainingSeconds: FITNESS_GUIDED_WARMUP_SECONDS };
  if (action === 'tick' && value.stage === 'warmup' && value.remainingSeconds > 0) return { ...value, remainingSeconds: value.remainingSeconds - 1 };
  return current;
}
